import { DefaultQueryParams } from '@frontend/api-utils';
import { Filter } from '@frontend/components/common';
import { ModalType, useModals } from '@frontend/modal';
import { changeFilter, changeFilters, changePage, useListRepository } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React from 'react';

import FilterModal from './filter-modal/filter-modal.component';
import useHeaderFilter from './header-filter.controller';

export interface Props<T extends object & { id: string }> extends ReturnType<typeof useListRepository<T>> {
    dispatch: ThunkDispatch<any, any, Action>;
    filter?: FilterOptions;
    setOptionsModalOpen?: (open: boolean) => void;
}

// eslint disabled because T is not used but probably will be in the future
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
export interface FilterOptions {
    keys?: (DefaultQueryParams | string | number)[];
    default?: boolean;
    keyProps?: Map<string, any>;
    queryParamKeyOverwrite?: Map<string, string>;
    booleanKeys?: string[];
    hideKeys?: (string | number)[];
    type?: 'platform' | 'terminal';
    defaultValues?: Map<string, any>;
}

export const HeaderFilter = <T extends object & { id: string }>(props: Props<T>) => {
    const viewProps = useHeaderFilter(props);
    const { openModal, closeModal } = useModals();
    const defaultSearchKey = props.filter?.queryParamKeyOverwrite?.get('search') ?? 'search';
    return (
        <div className='inline-flex h-[44px] items-center justify-start gap-3'>
            {props.filter?.default && (
                <Filter
                    label={null}
                    placeholder='Search in table...'
                    className='bg-background-surface h-full self-center'
                    value={getFilterValue(viewProps.filters?.[defaultSearchKey])}
                    filterValue={(value) => {
                        props.dispatch(
                            changeFilter({
                                listId: props.listId,
                                key: defaultSearchKey,
                                value: value
                            })
                        );
                        props.dispatch(changePage({ listId: props.listId, page: 0 }));
                    }}
                />
            )}
            {(props.filter?.type === 'platform' || props.filter?.type === undefined) && props.filter?.keys && props.filter.keys.length > 0 && (
                <div
                    data-testid='filter-button'
                    className='hover:bg-background-default bg-background-surface outline-border-default relative inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 outline-1 outline-offset-[-1px] hover:cursor-pointer'
                    onClick={() => {
                        openModal(
                            'filter',
                            () => (
                                <FilterMod
                                    props={props}
                                    viewProps={viewProps}
                                    handleClose={() => closeModal('filter')}
                                />
                            ),
                            {},
                            { type: ModalType.PANE, customWidth: 30 }
                        );
                    }}>
                    <div className='text-brand-muted justify-start text-base font-medium'>Filter</div>
                    {viewProps.filters && Object.keys(viewProps.filters).filter((p) => p !== defaultSearchKey).length > 0 && (
                        <span
                            className='bg-brand-primary bg-info absolute top-0.5 right-0.5 grid min-h-[24px] min-w-[24px] place-items-center rounded-full px-1 py-1 text-xs text-white'
                            style={{ top: 0, right: 0, transform: 'translateX(50%) translateY(-50%)' }}>
                            {Object.keys(viewProps.filters).filter((p) => p !== defaultSearchKey).length}
                        </span>
                    )}
                </div>
            )}
            {/* Table options are hidden for now because column configuration is not used yet.
            <Dropdown
                hideChevron
                iconClass='me-0'
                icon={() => <LuEllipsisVertical className='text-brand-muted size-5' />}
                buttonStyle='size-11 hover:bg-background-default hover:cursor-pointer px-4 bg-background-surface rounded-xl outline outline-1 outline-offset-[-1px] outline-border-default inline-flex justify-center items-center gap-2.5'>
                <DropdownItem
                    label='Options'
                    onClick={() => props.setOptionsModalOpen && props.setOptionsModalOpen(true)}
                />
            </Dropdown>
            */}
        </div>
    );
};

const getFilterValue = (value: unknown): string | null => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value[0] ?? null;
    if (value === undefined || value === null) return null;
    return String(value);
};

const FilterMod = <T extends object & { id: string }>(props: { props: Props<T>; viewProps: ReturnType<typeof useHeaderFilter>; handleClose: () => void }) => {
    const defaultSearchKey = props.props.filter?.queryParamKeyOverwrite?.get('search') ?? 'search';
    return (
        <FilterModal
            handleClose={props.handleClose}
            changeURLParams={(v) => {
                if (Object.keys(v).length === 0) {
                    props.props.dispatch(
                        changeFilter({
                            listId: props.props.listId,
                            key: defaultSearchKey,
                            value: props.viewProps.filters ? props.viewProps.filters[defaultSearchKey] : undefined
                        })
                    );
                    if (props.viewProps.filters) {
                        Object.keys(props.viewProps.filters).forEach((k) => {
                            if (k === defaultSearchKey) return;
                            props.props.dispatch(changeFilter({ listId: props.props.listId, key: k, value: null }));
                        });
                    }
                } else {
                    const obj = { ...props.viewProps.filters, ...v };
                    const cleanObj = Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
                    props.props.dispatch(
                        changeFilters({
                            listId: props.props.listId,
                            filters: Object.entries(cleanObj).map(([key, value]) => {
                                return { key, value };
                            })
                        })
                    );
                }
            }}
            {...props.props}
        />
    );
};
